import { Check, CircleHelp, Loader2, Mic, Square, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { logAudio, rumAction } from '@/lib/rum';
import { cn } from '@/lib/utils';
import {
  asBool,
  asText,
  buildMicCheckLog,
  playedThrough,
  summariseTake,
  verdictsFor,
  CLIP_LEVEL,
  QUIET_MS,
  SPEAK_MS,
} from './mic-check';
import type {
  CaptureSettings,
  MicCheckLog,
  MicFrame,
  SdrVerdict,
  TakeFailure,
  VerdictRow,
} from './mic-check';
import type { MicState } from './mic-health';

// The narrow slice of the softphone this needs, so a dialog on the pool picker
// cannot reach call state.
export interface MicCheckPhone {
  micState: MicState;
  currentInputId: string | null;
  currentOutputId: string | null;
  outputSelectionSupported: boolean;
  selectInputDevice: (deviceId: string) => void;
  recheckMic: () => void;
  releaseMic: () => Promise<void>;
  restoreMic: () => Promise<boolean>;
}

type Phase = 'idle' | 'releasing' | 'speaking' | 'quiet' | 'restoring' | 'result' | 'failed';

// ~43ms at 48kHz, so consecutive animation frames overlap rather than leaving gaps
// between the windows they measure.
const FFT_SIZE = 2048;

// How often the countdown redraws. The samples arrive at ~60Hz and must not each
// cost a render.
const TICK_MS = 200;
// Long enough for a healthy recorder to flush, short enough that a wedged one does
// not hold the microphone.
const FLUSH_DEADLINE_MS = 2_000;
const ACQUIRE_RETRY_MS = 500;

const STUCK_AFTER_TAKES = 3;

interface Take {
  rows: VerdictRow[];
  log: MicCheckLog;
  audioUrl: string | null;
  played: boolean;
  // A recording that won't play can't be listened to, so listening stops being required.
  audioFailed: boolean;
}

function withheldReason(failure: TakeFailure | null, failedStreak: number): string {
  if (failure === null) return 'Listen to the whole recording first.';
  if (failure === 'unmeasured') {
    return 'We couldn’t measure that take — let it run to the end with this tab in front.';
  }
  const stuck = failedStreak >= STUCK_AFTER_TAKES ? ' Still failing? Tell your team lead.' : '';
  return `We can’t pass this take — fix the problem marked above and try again.${stuck}`;
}

// A rejected play is the autoplay policy; the controls are still there to press.
const play = (el: HTMLAudioElement): void => void el.play().catch(() => undefined);

// A recorder writes its container metadata before the recording exists, so the
// blob carries no duration and the native controls come up with no timeline and no
// seek. Reading past the end forces the real duration to be computed.
function loadAndPlay(e: React.SyntheticEvent<HTMLAudioElement>): void {
  const el = e.currentTarget;
  if (Number.isFinite(el.duration)) return play(el);
  const onSeeked = (): void => {
    el.removeEventListener('seeked', onSeeked);
    el.currentTime = 0;
    play(el);
  };
  el.addEventListener('seeked', onSeeked);
  el.currentTime = 1e101;
}

function heardRanges(el: HTMLAudioElement): [number, number][] {
  return Array.from({ length: el.played.length }, (_, i) => [el.played.start(i), el.played.end(i)]);
}

function RowIcon({ verdict }: { verdict: VerdictRow['verdict'] }) {
  if (verdict === 'ok') return <Check className="mt-0.5 size-4 shrink-0 text-emerald-600" />;
  if (verdict === 'bad') return <X className="text-destructive mt-0.5 size-4 shrink-0" />;
  return <CircleHelp className="text-muted-foreground mt-0.5 size-4 shrink-0" />;
}

export function MicCheckDialog({
  open,
  onClose,
  onAllGood,
  phone,
}: {
  open: boolean;
  onClose: () => void;
  onAllGood: () => void;
  phone: MicCheckPhone;
}) {
  const ref = useRef<HTMLDialogElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [take, setTake] = useState<Take | null>(null);
  const [sinkFailed, setSinkFailed] = useState(false);
  // Not reset on close: an SDR who gives up and reopens is still stuck.
  const [failedStreak, setFailedStreak] = useState(0);

  const streamRef = useRef<MediaStream | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  // Retained only so it cannot be collected mid-take. A collected source stops
  // feeding the analyser, and a take with no samples reads as a dead microphone.
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  // Bumped on every start and abort. Anything resuming after an await checks it and
  // bails if a newer run has begun, so two takes can never share the refs below.
  const runRef = useRef(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const rafRef = useRef(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const framesRef = useRef<MicFrame[]>([]);
  const startedAtRef = useRef(0);
  const stoppedAtRef = useRef(0);
  const heldRef = useRef(false);
  // The restore in flight, so leaving mid-restore joins it instead of starting a
  // second one that would race the first for the same device.
  const restoreRef = useRef<Promise<boolean> | null>(null);
  const finishingRef = useRef(false);
  const takeNoRef = useRef(0);
  // The take on screen, held so the log row can be written when the SDR says what
  // they think of it — one row per take, carrying both verdicts.
  const pendingRef = useRef<Take | null>(null);
  const urlRef = useRef<string | null>(null);
  // What the browser actually gave us, read off the live track. Every level the
  // check reports describes this processing chain, not the raw microphone.
  const captureRef = useRef<{ settings: CaptureSettings; deviceId: string | null }>({
    settings: {
      echoCancellation: null,
      echoCancellationType: null,
      noiseSuppression: null,
      autoGainControl: null,
      sampleRate: null,
      channelCount: null,
      mimeType: null,
    },
    deviceId: null,
  });

  const clearTimers = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  // Order matters: the recorder must be allowed to flush before the tracks that
  // feed it are stopped, or the blob comes back empty.
  const teardownCapture = useCallback(async (): Promise<Blob | null> => {
    clearTimers();
    const recorder = recorderRef.current;
    recorderRef.current = null;
    let blob: Blob | null = null;
    if (recorder && recorder.state !== 'inactive') {
      const flushed = new Promise<Blob | null>((resolve) => {
        const chunks: Blob[] = [];
        recorder.addEventListener('dataavailable', (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        });
        recorder.addEventListener('stop', () =>
          resolve(chunks.length > 0 ? new Blob(chunks, { type: recorder.mimeType }) : null),
        );
        recorder.addEventListener('error', () => resolve(null));
        try {
          recorder.stop();
        } catch {
          resolve(null);
        }
      });
      // Never wait on it forever. Everything past this point hands the microphone
      // back, so a recorder that goes quiet — a device pulled mid-take is the live
      // case — must cost the playback, not the mic.
      blob = await Promise.race([
        flushed,
        new Promise<Blob | null>((r) => setTimeout(() => r(null), FLUSH_DEADLINE_MS)),
      ]);
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    sourceRef.current = null;
    const ctx = ctxRef.current;
    ctxRef.current = null;
    if (ctx) await ctx.close().catch(() => undefined);
    return blob;
  }, [clearTimers]);

  const releaseUrl = useCallback(() => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
  }, []);

  const logTake = useCallback((sdrVerdict: SdrVerdict) => {
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    const row = { ...pending.log, sdr_verdict: sdrVerdict, played_through: pending.played };
    logAudio('mic_check', row);
    rumAction('cockpit.mic.check', {
      v_captured: row.v_captured,
      v_clipping: row.v_clipping,
      v_noise: row.v_noise,
      v_device: row.v_device,
      sdr_verdict: sdrVerdict,
      played_through: row.played_through,
    });
  }, []);

  // Keyed by the recording, so a late event from the element of an earlier take
  // cannot mark this one.
  const markTake = useCallback(
    (audioUrl: string, patch: Pick<Partial<Take>, 'played' | 'audioFailed'>) => {
      const pending = pendingRef.current;
      if (pending?.audioUrl === audioUrl) pendingRef.current = { ...pending, ...patch };
      setTake((t) => (t?.audioUrl === audioUrl ? { ...t, ...patch } : t));
    },
    [],
  );

  // The one way the mic goes back, shared by finishing and by leaving. Ownership is
  // only given up once the restore has actually resolved, and a second caller joins
  // the same attempt rather than starting a competing one.
  const handBack = useCallback(async (): Promise<boolean> => {
    if (restoreRef.current) return restoreRef.current;
    if (!heldRef.current) return true;
    const p = phone.restoreMic().finally(() => {
      heldRef.current = false;
      restoreRef.current = null;
    });
    restoreRef.current = p;
    return p;
  }, [phone]);

  const finishTake = useCallback(
    async (stale: () => boolean) => {
      const blob = await teardownCapture();
      if (heldRef.current) {
        setPhase('restoring');
        const restored = await handBack();
        // A take abandoned while the mic was coming back must not paint over whatever
        // the SDR is looking at now.
        if (stale()) return;
        if (!restored) {
          setError('We couldn’t get your microphone back.');
          setPhase('failed');
          return;
        }
      }
      const stoppedAtMs = stoppedAtRef.current > 0 ? stoppedAtRef.current : SPEAK_MS + QUIET_MS;
      const metrics = summariseTake(framesRef.current, {
        speakMs: Math.min(SPEAK_MS, stoppedAtMs),
        stoppedAtMs,
      });
      // Read from the browser rather than from the softphone's cached list: the
      // recording is the thing being judged, so the device it actually opened is the
      // one that belongs on the row.
      const inputs = (await navigator.mediaDevices.enumerateDevices().catch(() => []))
        .filter((d) => d.kind === 'audioinput')
        .map((d) => ({ deviceId: d.deviceId, label: d.label, groupId: d.groupId }));
      const selectedId = captureRef.current.deviceId ?? phone.currentInputId;
      const rows = verdictsFor(metrics, inputs, selectedId);
      const log = buildMicCheckLog({
        metrics,
        rows,
        inputs,
        selectedId,
        capture: captureRef.current.settings,
        platform: navigator.platform || null,
        take: takeNoRef.current,
        sdrVerdict: 'closed',
      });
      if (stale()) return;
      releaseUrl();
      const audioUrl = blob ? URL.createObjectURL(blob) : null;
      urlRef.current = audioUrl;
      setFailedStreak((n) => (log.failure === 'bad' ? n + 1 : log.failure === null ? 0 : n));
      const next: Take = { rows, log, audioUrl, played: false, audioFailed: false };
      pendingRef.current = next;
      setTake(next);
      setPhase('result');
    },
    [handBack, phone, releaseUrl, teardownCapture],
  );

  // The stop button and the end-of-take timer both land here, and `phase` does not
  // move until the teardown inside has awaited — so the latch has to be synchronous
  // or a second entry tears down the stream the first is still flushing.
  const finish = useCallback(async () => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    const run = runRef.current;
    try {
      await finishTake(() => run !== runRef.current);
    } finally {
      finishingRef.current = false;
    }
  }, [finishTake]);

  const start = useCallback(async () => {
    // Tear down anything already running before claiming this run: a second Try
    // again lands here while the first take still holds a stream, and overwriting
    // the refs would leave that microphone open until the page unloads.
    await abortRef.current();
    const run = ++runRef.current;
    const stale = (): boolean => run !== runRef.current;

    setError(null);
    setTake(null);
    setElapsed(0);
    framesRef.current = [];
    stoppedAtRef.current = 0;
    takeNoRef.current += 1;
    setPhase('releasing');

    // Claimed BEFORE the await. If the dialog closes or the picker unmounts while
    // the release is in flight, the teardown has to know the mic is out on loan —
    // otherwise nothing hands it back and the softphone never re-acquires.
    heldRef.current = true;
    await phone.releaseMic();
    if (stale()) return;

    // Defaults on purpose — echo cancellation, noise suppression and gain control
    // are what a call applies, and a recording without them would flatter or damn a
    // microphone that behaves differently in the only situation that matters.
    const want: MediaStreamConstraints['audio'] = phone.currentInputId
      ? { deviceId: { exact: phone.currentInputId } }
      : true;
    let stream: MediaStream | null = null;
    let lastErr: unknown = null;
    // The device was force-released microseconds ago, so a first rejection is more
    // likely to be an OS that has not finished freeing it than a broken mic. The
    // last attempt drops the exact-device constraint rather than telling a working
    // SDR their microphone is unavailable; the device row still names whatever
    // actually opened, so the fallback cannot lie about what was recorded.
    for (const attempt of [want, want, true] as const) {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: attempt });
        break;
      } catch (err: unknown) {
        lastErr = err;
        await new Promise((r) => setTimeout(r, ACQUIRE_RETRY_MS));
        if (stale()) return;
      }
    }
    if (!stream) {
      setError(
        `We couldn’t get your microphone: ${lastErr instanceof Error ? lastErr.message : 'unavailable'}`,
      );
      setPhase('failed');
      await handBack();
      return;
    }
    if (stale()) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    streamRef.current = stream;

    const s = stream.getAudioTracks()[0]?.getSettings() ?? {};
    captureRef.current = {
      deviceId: s.deviceId ?? null,
      settings: {
        echoCancellation: asBool(s.echoCancellation),
        echoCancellationType: asText(
          (s as { echoCancellationType?: unknown }).echoCancellationType,
        ),
        noiseSuppression: asBool(s.noiseSuppression),
        autoGainControl: asBool(s.autoGainControl),
        sampleRate: s.sampleRate ?? null,
        channelCount: s.channelCount ?? null,
        mimeType: null,
      },
    };

    const ctx = new AudioContext();
    ctxRef.current = ctx;
    if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined);
    if (stale()) return;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = FFT_SIZE;
    // Into the analyser and no further: connecting on to the destination would put
    // the SDR's own microphone through their speakers.
    sourceRef.current = ctx.createMediaStreamSource(stream);
    sourceRef.current.connect(analyser);

    if (typeof MediaRecorder !== 'undefined') {
      try {
        const recorder = new MediaRecorder(stream);
        captureRef.current.settings.mimeType = recorder.mimeType || null;
        recorder.start();
        recorderRef.current = recorder;
      } catch {
        // No playback, but the measurements below still stand.
      }
    }

    const buf = new Float32Array(analyser.fftSize);
    startedAtRef.current = performance.now();
    setPhase('speaking');

    const sample = (): void => {
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      let peak = 0;
      let clipped = 0;
      for (const v of buf) {
        const a = Math.abs(v);
        sum += v * v;
        if (a > peak) peak = a;
        if (a >= CLIP_LEVEL) clipped += 1;
      }
      framesRef.current.push({
        t: performance.now() - startedAtRef.current,
        rms: Math.sqrt(sum / buf.length),
        peak,
        clipped,
        samples: buf.length,
      });
      rafRef.current = requestAnimationFrame(sample);
    };
    rafRef.current = requestAnimationFrame(sample);
    tickRef.current = setInterval(
      () => setElapsed(performance.now() - startedAtRef.current),
      TICK_MS,
    );

    // The beats are driven by timers, never by the sampling loop: animation frames
    // stop entirely in a background tab, which would leave the take running and the
    // microphone held for as long as the SDR looked away.
    timersRef.current.push(setTimeout(() => setPhase('quiet'), SPEAK_MS));
    timersRef.current.push(
      setTimeout(() => {
        stoppedAtRef.current = performance.now() - startedAtRef.current;
        void finish();
      }, SPEAK_MS + QUIET_MS),
    );
  }, [finish, phone]);

  const stopEarly = useCallback(() => {
    // Only while something is actually being recorded: a second click would
    // otherwise run the teardown concurrently with the first and truncate the blob.
    if (phase !== 'speaking' && phase !== 'quiet') return;
    stoppedAtRef.current = performance.now() - startedAtRef.current;
    void finish();
  }, [finish, phase]);

  // Everything that has to happen however the SDR leaves — the close button, Esc,
  // the backdrop, or the picker being unmounted underneath them. Held in a ref so
  // the unmount cleanup runs the current version rather than the one from mount.
  const abortRef = useRef<() => Promise<void>>(async () => undefined);
  const startRef = useRef<() => Promise<void>>(async () => undefined);
  useEffect(() => {
    startRef.current = start;
    abortRef.current = async () => {
      // Invalidates any take still resuming from an await, so a late continuation
      // cleans up after itself instead of overwriting what happens next.
      runRef.current += 1;
      await teardownCapture();
      await handBack();
      releaseUrl();
    };
  });

  // The single exit. Every way out of this dialog closes the element, and the
  // element's own close event routes all of them through here — so there is one
  // teardown to keep correct rather than one per button.
  const dismiss = useCallback(() => {
    logTake('closed');
    void abortRef.current();
    setPhase('idle');
    setTake(null);
    onClose();
  }, [logTake, onClose]);

  const close = useCallback(() => ref.current?.close(), []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
      // Through the ref: this must fire on the OPEN transition alone. Re-running it
      // because a callback identity changed would restart a take mid-recording.
      void startRef.current();
    }
    if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.addEventListener('close', dismiss);
    return () => el.removeEventListener('close', dismiss);
  }, [dismiss]);

  // A phase change can unmount the picker under an open dialog. Without this the
  // microphone would be left released with nothing to hand it back.
  useEffect(() => () => void abortRef.current(), []);

  // Playback follows the SDR's chosen speaker. On the OS default they may hear
  // nothing at all, which would read as a dead microphone.
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !phone.outputSelectionSupported || !phone.currentOutputId) return;
    // A rejection here (permission, or a policy that blocks output selection) means
    // playback comes out of the system default instead — which an SDR hears as
    // silence and reads as a dead microphone, so it is said rather than swallowed.
    // Re-judged on every attempt: a warning that outlives the problem sends them to
    // troubleshoot a speaker that is now working.
    void el.setSinkId(phone.currentOutputId).then(
      () => setSinkFailed(false),
      () => setSinkFailed(true),
    );
  }, [take, phone.outputSelectionSupported, phone.currentOutputId]);

  const canConfirm =
    take !== null &&
    take.log.failure === null &&
    (!take.audioUrl || take.audioFailed || take.played);

  const remaining =
    phase === 'speaking'
      ? Math.max(0, Math.ceil((SPEAK_MS - elapsed) / 1000))
      : Math.max(0, Math.ceil((SPEAK_MS + QUIET_MS - elapsed) / 1000));

  return (
    <dialog
      ref={ref}
      className="bg-background text-foreground m-auto w-[min(34rem,calc(100vw-2rem))] rounded-lg border p-0 shadow-lg backdrop:bg-black/50"
      data-testid="mic-check-dialog"
      aria-labelledby="mic-check-title"
    >
      <div className="space-y-4 p-5 text-sm">
        <div className="flex items-center gap-2">
          <Mic className="size-5" />
          <h2 id="mic-check-title" className="text-base font-semibold">
            How you sound to prospects
          </h2>
        </div>

        {phase === 'releasing' && (
          <p className="text-muted-foreground flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" /> Getting your microphone…
          </p>
        )}

        {(phase === 'speaking' || phase === 'quiet') && (
          <div className="space-y-3">
            <p className="text-base font-medium">
              {phase === 'speaking' ? 'Say your usual opener' : 'Now stay quiet…'}
            </p>
            <p className="text-muted-foreground">
              {phase === 'speaking'
                ? 'Talk as you would on a call — normal voice, normal distance.'
                : 'A couple of seconds of silence lets us hear what’s behind you.'}
            </p>
            <div className="flex items-center gap-3">
              <span className="tabular-nums text-lg font-semibold">{remaining}s</span>
              <Button variant="outline" size="sm" onClick={stopEarly}>
                <Square /> Stop
              </Button>
            </div>
          </div>
        )}

        {phase === 'restoring' && (
          <p className="text-muted-foreground flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" /> Restoring your microphone…
          </p>
        )}

        {phase === 'failed' && (
          <div className="space-y-3">
            <p className="text-destructive">{error}</p>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => void start()}>
                Try again
              </Button>
              <Button size="sm" variant="outline" onClick={phone.recheckMic}>
                Re-check microphone
              </Button>
            </div>
          </div>
        )}

        {phase === 'result' && take && (
          <div className="space-y-4">
            {take.audioUrl && !take.audioFailed ? (
              <audio
                ref={audioRef}
                controls
                src={take.audioUrl}
                className="w-full"
                onLoadedMetadata={loadAndPlay}
                onEnded={(e) => {
                  const el = e.currentTarget;
                  // A muted element still accumulates played ranges.
                  const audible = !el.muted && el.volume > 0;
                  if (audible && playedThrough(heardRanges(el), el.duration)) {
                    markTake(el.src, { played: true });
                  }
                }}
                onError={(e) => markTake(e.currentTarget.src, { audioFailed: true })}
              />
            ) : (
              <p className="text-muted-foreground">
                We couldn’t play that back in this browser — the checks below still ran.
              </p>
            )}
            {sinkFailed && (
              <p className="text-xs text-amber-700">
                This is playing through your system’s default speaker, not the one picked in the
                audio panel — check there if you can’t hear anything.
              </p>
            )}
            {take.log.resolved_label && (
              <p className="text-muted-foreground text-xs">
                Recorded from <span className="font-medium">{take.log.resolved_label}</span>. A
                recording here is higher quality than a phone line, so judge it for clarity rather
                than richness.
              </p>
            )}

            <ul className="space-y-2">
              {take.rows.map((row) => {
                const switchTo = row.switchToDeviceId;
                return (
                  <li key={row.id} className="flex gap-2" data-testid={`mic-check-${row.id}`}>
                    <RowIcon verdict={row.verdict} />
                    <div className="flex-1">
                      <div
                        className={cn(
                          'font-medium',
                          row.verdict === 'bad' && 'text-destructive',
                          row.verdict === 'unknown' && 'text-muted-foreground',
                        )}
                      >
                        {row.title}
                      </div>
                      <p className="text-muted-foreground">{row.body}</p>
                      {switchTo && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-1.5"
                          onClick={() => phone.selectInputDevice(switchTo)}
                        >
                          Switch
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="border-t pt-3">
              <p className="font-medium">Does that sound like you?</p>
              <p className="text-muted-foreground">
                If it’s muffled or quiet, try re-plugging your headset and moving the mic clear of
                your clothing, in front of your face.
              </p>
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  data-testid="mic-check-ok"
                  disabled={!canConfirm}
                  onClick={() => {
                    // Logged before closing: `dismiss` records whatever verdict is
                    // still pending, and this one is no longer 'closed'.
                    logTake('ok');
                    onAllGood();
                    close();
                  }}
                >
                  <Check /> All good
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    logTake('retry');
                    void start();
                  }}
                >
                  Try again
                </Button>
              </div>
              {!canConfirm && (
                <p
                  className={cn(
                    'mt-2',
                    take.log.failure ? 'text-destructive' : 'text-muted-foreground',
                  )}
                  data-testid="mic-check-withheld"
                >
                  {withheldReason(take.log.failure, failedStreak)}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end border-t pt-3">
          <Button size="sm" variant="ghost" onClick={close}>
            Close
          </Button>
        </div>
      </div>
    </dialog>
  );
}
